import { bridge } from "./foyerBridge";
import { fetchGuestCastingCredentials } from "../hooks/useGuestCasting";
import { CONFIG_CACHE_PREFIX } from "../hooks/useConfig";
import { BINDING_KEYS, durableRemove } from "./binding";
import { DEDUPE_KEY } from "./commandDedupe";

/**
 * TV side of checkout hygiene (server: services/checkoutHygiene.ts).
 *
 * When a room turns over, the server has already rotated that room's cast
 * hotspot credentials and cut its companion sessions. This is what makes the
 * *device* agree with that: the SoftAP is still broadcasting the departing
 * guest's password until we re-apply, and the screen is still sitting on
 * whatever page/language/cached content they left it on.
 *
 * What we clear, honestly (full matrix in docs/checkout-hygiene.md):
 *   ✔ the guest cast receiver + its credentials - torn down, re-applied fresh
 *   ✔ cached premise content in our own web storage
 *   ✔ page + language, via the reload (both are in-memory state that resets to
 *     the premise default on boot)
 *   ✘ logins inside native apps (Netflix, YouTube, Spotify…). Those live in the
 *     platform's own app sandboxes; a WebView cannot reach them. Only the
 *     platform's hospitality reset clears those, and we never pretend otherwise.
 *   ✘ the offline shell cache (public/sw.js). Deliberately: it holds the app
 *     bundle and the index HTML, not one byte of guest or premise data, and
 *     dropping it would leave a just-turned-over room unable to boot if the WAN
 *     happened to be down five minutes later.
 *
 * Everything is best-effort and independently guarded: a wipe must degrade to
 * "did what it could" rather than leaving the TV half-reset on the first throw.
 */

/**
 * Keys we must NOT clear:
 *  - the binding/screen id - wiping it would unpair the TV and drop it to the
 *    pairing screen at the worst possible moment (mid-turnover);
 *  - the executed-command set - the wipe reloads the page, and a reload that
 *    re-drains its own command with an empty dedupe set is an infinite
 *    wipe→reload loop.
 * Neither carries a byte of guest data.
 */
const PRESERVED_KEYS: readonly string[] = [...BINDING_KEYS, DEDUPE_KEY];

/** Drop guest-scoped web storage: our cached premise content and anything else
 *  under the `foyer.` namespace that isn't device identity. */
export function clearGuestLocalState(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      const isOurs =
        key.startsWith(CONFIG_CACHE_PREFIX) || key.startsWith("foyer.");
      if (isOurs && !PRESERVED_KEYS.includes(key)) doomed.push(key);
    }
    // `durableRemove` clears BOTH stores. The premise config now has a native
    // (SharedPreferences) copy so it survives a reboot with the WAN down - which
    // means a localStorage-only wipe would leave the departing guest's room
    // content durably on the device and restore it on the next boot.
    for (const key of doomed) durableRemove(key);
  } catch {
    // Storage unavailable - the reload below still resets in-memory state.
  }
  try {
    sessionStorage.clear();
  } catch {
    // Same: best-effort.
  }
}

/**
 * Tear the guest cast receiver down and bring it back up on the rotated
 * credentials. The teardown is the part that matters for privacy - it is what
 * kicks a still-connected phone off the room's hotspot; the re-apply is what
 * leaves the room ready for the next guest.
 *
 * Returns true when new credentials were applied. False means the platform has
 * no credential surface (Tizen/webOS mirroring, no casting at all) or the fetch
 * failed - the server-side rotation stands either way, so the stale password is
 * dead as soon as the device does get to re-read.
 */
export async function reapplyGuestCasting(): Promise<boolean> {
  try {
    if (!bridge.capabilities().guestCasting) return false;
    // Always tear down first, even if we can't fetch new credentials: an AP
    // that is off is safer than one still broadcasting the old password.
    bridge.disableGuestCasting();
    if (bridge.platform !== "android") return false;
    const creds = await fetchGuestCastingCredentials();
    if (!creds) return false;
    return bridge.enableGuestCasting(creds);
  } catch {
    return false;
  }
}

/** What the device did, reported back to the server as the command's ack
 *  result so the property's audit trail records hardware truth. */
export interface CheckoutWipeReport {
  /** Cast receiver torn down and re-applied on the rotated credentials. */
  castingReapplied: boolean;
  /** Our own cached content + guest-scoped local state cleared. */
  localStateCleared: boolean;
  /** Did the FIRMWARE confirm it cleared native app logins? */
  credentialsCleared: boolean;
  /** Which mechanism ran ("jedi-pms", "tizen-b2b:…", "hcap:…", "none"). */
  credentialMethod: string;
  /** Why, when it didn't. */
  credentialDetail: string;
  platform: string;
}

/**
 * Execute a `checkout_wipe` and report what actually happened.
 *
 * Does NOT reload - the caller owns that, because it must ack this report
 * first (a reload kills the in-flight ack, and an un-acked wipe gets re-drained
 * and re-run). Order matters within the wipe too: the firmware clear runs
 * BEFORE we tear our own state down, so a firmware that reboots or drops the
 * WebView mid-clear can't take the rest of the wipe with it.
 */
export async function runCheckoutWipe(): Promise<CheckoutWipeReport> {
  // 1. The firmware's own guest-data clear (native app logins). This is the
  //    only step that can reach outside our sandbox - see foyerBridge.
  const creds = await bridge.clearGuestCredentials();
  // 2. Our credentials: kick any still-connected phone off the room's hotspot
  //    and come back up on the rotated password.
  const castingReapplied = await reapplyGuestCasting();
  // 3. Our own state.
  clearGuestLocalState();

  return {
    castingReapplied,
    localStateCleared: true,
    credentialsCleared: creds.cleared,
    credentialMethod: creds.method,
    credentialDetail: creds.detail,
    platform: creds.platform,
  };
}
